import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field, inputCls } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

const CONSIDERING = ["Memorial diamond", "Diamond set in jewelry", "Keepsake jewelry", "Not sure yet"];
const COLORS = ["Colorless", "Blue", "Yellow", "Pink", "Black", "Not sure"];
const SIZES = ["Under 0.5 ct", "0.5 – 1 ct", "1 – 2 ct", "Over 2 ct", "Not sure"];
const SETTINGS = ["Ring", "Pendant", "Earrings", "Bracelet", "Loose diamond", "Not sure"];

const Opt = ({ value, opts, onChange, testId }: any) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
    <option value="">Select</option>{opts.map((o: string) => <option key={o}>{o}</option>)}
  </select>
);

export default function MemorialEnquiry() {
  useMeta("Memorial Enquiry | Somnio.Co", "Begin a private, no-obligation conversation about a memorial diamond for a loved one or pet.");
  const [f, setF] = useState<any>({ name: "", email: "", phone: "", for_whom: "person", loved_one: "", considering: CONSIDERING[0], contact_pref: "Email", color: "", size: "", setting: "", message: "" });
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k: string) => (v: any) => setF({ ...f, [k]: typeof v === "string" ? v : v.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/memorial/enquiries", f); setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="memorial-enquiry-success">
      <h1 className="font-display text-4xl font-light">Thank you, {f.name.split(" ")[0]}</h1>
      <p className="mt-5 text-neutral-600">We've received your enquiry. A member of our memorial team will contact you personally within one business day. There is no obligation, and everything you've shared stays private.</p>
    </div>
  );
  return (
    <div className="pb-24">
      <PageHeader eyebrow="Memorial Diamonds" title="A Private Enquiry" intro={<>Share as much or as little as you're comfortable with.<br />Our memorial team will reach out gently, at a pace that feels right for you.</>} />
      <form onSubmit={submit} className="max-w-3xl mx-auto px-5 md:px-12 space-y-6" data-testid="memorial-enquiry-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Your name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="me-name" /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="me-email" /></Field>
          <Field label="Phone (optional)"><input type="tel" value={f.phone} onChange={set("phone")} className={inputCls} data-testid="me-phone" /></Field>
          <Field label="Best way to reach you"><Opt value={f.contact_pref} opts={["Email", "Phone", "Either"]} onChange={set("contact_pref")} testId="me-contact" /></Field>
        </div>
        <div>
          <span className="eyebrow text-neutral-500 block mb-3">This tribute is for</span>
          <div className="grid grid-cols-2 gap-3">
            {[["person", "A loved one"], ["pet", "A beloved pet"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setF({ ...f, for_whom: k })} className={`py-4 border text-sm transition-colors ${f.for_whom === k ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`} data-testid={`me-for-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Their name (optional)"><input value={f.loved_one} onChange={set("loved_one")} className={inputCls} data-testid="me-loved-one" /></Field>
          <Field label="What are you considering?"><Opt value={f.considering} opts={CONSIDERING} onChange={set("considering")} testId="me-considering" /></Field>
        </div>
        <div className="border border-neutral-200">
          <button type="button" onClick={() => setMore(!more)} className="w-full flex items-center justify-between px-5 py-4 text-sm" data-testid="me-prefs-toggle">
            <span>Diamond preferences <span className="text-neutral-500">(optional)</span></span><ChevronDown size={16} className={`transition-transform ${more ? "rotate-180" : ""}`} />
          </button>
          {more && (
            <div className="grid sm:grid-cols-3 gap-4 px-5 pb-5" data-testid="me-prefs">
              <Field label="Color"><Opt value={f.color} opts={COLORS} onChange={set("color")} testId="me-color" /></Field>
              <Field label="Size"><Opt value={f.size} opts={SIZES} onChange={set("size")} testId="me-size" /></Field>
              <Field label="Setting"><Opt value={f.setting} opts={SETTINGS} onChange={set("setting")} testId="me-setting" /></Field>
            </div>
          )}
        </div>
        <Field label="Anything you'd like us to know (optional)"><textarea rows={5} value={f.message} onChange={set("message")} className={inputCls} data-testid="me-message" /></Field>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={set("website")} />
        <p className="text-xs text-neutral-600">Your details are kept private and only used by our memorial team to contact you.</p>
        <button disabled={busy} className="btn-ink w-full py-4 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60" data-testid="me-submit">{busy ? "Sending…" : "Send Private Enquiry"}</button>
      </form>
    </div>
  );
}
